import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock, Check, X, Pencil, Repeat } from 'lucide-react';
import { calendarService } from '../services/calendarService';
import apiService from '../services/apiService';
import { useLanguage } from '../i18n';
import { userErrorMessage } from '../utils/userError';
import useCompactWidth from '../hooks/useCompactWidth';
import { seriesSummary } from '../utils/calendarSeries';
import { CARD_ACTION_ROW, CARD_BTN_FIT, CARD_BTN_LABEL, CARD_ICON, CARD_COMPACT_WIDTH } from './cards/cardLayout';

const formatEventTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const formatTimeRange = (start, end) => {
    const s = formatEventTime(start);
    if (!end) return s;
    const e = new Date(end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${s} – ${e}`;
};

// Mirrors CalendarPanel's rule: a start that resolves to LOCAL midnight is the
// "Time to be Confirmed" sentinel (date known, clock time not given).
// "Time to be Confirmed" — the canonical two-part rule (#269), mirroring the BE's
// calendar_sharing.is_time_to_be_confirmed: a date-only event has NO end_time AND
// sits at local midnight. Testing midnight alone labelled a genuinely TIMED event
// "Time to be confirmed" whenever its start happened to render at 00:00 in the
// browser's timezone; an event with a duration provably had a clock time set.
const isTimePending = (start, end) => {
    if (!start) return true;
    if (end) return false;
    try {
        const d = new Date(start);
        return d.getHours() === 0 && d.getMinutes() === 0;
    } catch (_) { return false; }
};

const formatEventDate = (start) => {
    if (!start) return '';
    try {
        return new Date(start).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (_) { return ''; }
};

const STATUS_STYLES = {
    pending: {
        border: 'border-amber-300 dark:border-amber-700',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        badge: 'bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300',
        label: 'Pending',
    },
    confirmed: {
        border: 'border-green-300 dark:border-green-700',
        bg: 'bg-green-50 dark:bg-green-900/20',
        badge: 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300',
        label: 'Confirmed',
    },
    declined: {
        border: 'border-pai',
        bg: 'bg-white/[0.025]',
        badge: 'bg-white/[0.06] text-pai-meta',
        label: 'Declined',
    },
};

// t() key + English fallback for a booking status, mirroring STATUS_STYLES'
// labels exactly (reused from the Contacts Hub vocabulary).
const bookingStatusLabel = (status, t) => {
    const fallback = STATUS_STYLES[status]?.label || STATUS_STYLES.pending.label;
    return t(`contactEventStatus_${status}`, fallback);
};

const CalendarBookingCard = ({ event, conversationId, messageId }) => {
    const { t, language } = useLanguage();
    // #532: a repeating event is ONE card for the whole series — Confirm/Decline
    // apply to every occurrence (PATCH scope=all); per-occurrence edits happen in
    // the calendar or by asking PAI, so the card has no Edit for a series.
    const series = event.series && event.series.count ? event.series : null;
    const scopeArg = series ? { scope: 'all' } : {};
    // The user's Confirm/Decline is the source of truth (Ui Re up #82): once an action
    // is persisted into the message (event.resolution), the card locks to it and never
    // re-derives from the live event — so deleting the event later can't revert the
    // card to dead Confirm/Decline buttons.
    const [status, setStatus] = useState(event.resolution?.status || event.status || 'pending');
    // #545: a later, corrected card replaced this one (`superseded`) — the
    // resolution can arrive AFTER mount (the open chat is patched live), so a
    // still-pending card adopts it.
    const superseded = !!event.resolution?.superseded;
    const persistedStatus = event.resolution?.status;
    useEffect(() => {
        if (persistedStatus) setStatus((cur) => (cur === 'pending' ? persistedStatus : cur));
    }, [persistedStatus]);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    // Edit-save feedback (#268): {kind:'conflict', conflicts:[…]} | {kind:'error', message}
    const [editIssue, setEditIssue] = useState(null);
    const [cardRef, compact] = useCompactWidth(CARD_COMPACT_WIDTH);

    // Persist the action taken on THIS message's card so it survives reload.
    const persistResolution = (resolvedStatus) => {
        if (event) event.resolution = { status: resolvedStatus };
        if (conversationId && messageId) {
            // H-3.2: single resolver → generic card-resolution endpoint. calendar_event_created
            // accepts confirmed|declined (CARD_RESOLUTION_MAP).
            apiService.resolveCardState(conversationId, messageId, {
                card_key: 'calendar_event_created',
                status: resolvedStatus,
                // entry_id (Calendar Ui #226): a turn can render many booking cards
                // in one message — resolve THIS event's entry independently.
                entry_id: event?.event_id || undefined,
                action_id: event?.action_id || undefined,
            }).catch(() => { /* non-fatal */ });
        }
    };

    // Editable fields
    const [editTitle, setEditTitle] = useState(event.title || '');
    const [editStart, setEditStart] = useState(event.start_time ? event.start_time.slice(0, 16) : '');
    const [editEnd, setEditEnd] = useState(event.end_time ? event.end_time.slice(0, 16) : '');
    const [editLocation, setEditLocation] = useState(event.location || '');
    const [editDescription, setEditDescription] = useState(event.description || '');

    // Sync from the live event ONLY when the user hasn't acted on this card yet — e.g.
    // it was confirmed elsewhere. Once an action is recorded on the message we never
    // look at the event again (it may have been deleted). (Ui Re up #82)
    useEffect(() => {
        if (event.resolution?.status) return;
        if (event.event_id) {
            calendarService.getEvent(event.event_id)
                .then(data => { if (data?.status) setStatus(data.status); })
                .catch(() => {}); // ignore — use SSE status as fallback
        }
    }, [event.event_id, event.resolution?.status]);

    const handleConfirm = async () => {
        setSaving(true);
        try {
            await calendarService.updateEvent(event.event_id, { status: 'confirmed', ...scopeArg });
        } catch (err) {
            console.error('Failed to confirm event:', err);
        } finally {
            // Lock the card to the user's action regardless of the event's fate.
            setStatus('confirmed');
            persistResolution('confirmed');
            setSaving(false);
        }
    };

    const handleDecline = async () => {
        setSaving(true);
        try {
            await calendarService.updateEvent(event.event_id, { status: 'declined', ...scopeArg });
        } catch (err) {
            console.error('Failed to decline event:', err);
        } finally {
            setStatus('declined');
            persistResolution('declined');
            setSaving(false);
        }
    };

    const handleSaveEdit = async (force = false) => {
        setSaving(true);
        setEditIssue(null);
        try {
            await calendarService.updateEvent(event.event_id, {
                title: editTitle,
                start_time: editStart ? new Date(editStart).toISOString() : undefined,
                end_time: editEnd ? new Date(editEnd).toISOString() : undefined,
                location: editLocation || null,
                description: editDescription || null,
                status: 'confirmed',
                ...(force ? { force: true } : {}),
            });
            setStatus('confirmed');
            persistResolution('confirmed');
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to update event:', err);
            // Double-booking guard (#268): the BE now refuses a reschedule that
            // hard-overlaps a confirmed event with 409 {conflict:true}. Surface the
            // clash instead of silently doing nothing, and let the user insist.
            if (err?.status === 409 && err?.body?.conflict) {
                setEditIssue({ kind: 'conflict', conflicts: err.body.conflicts || [] });
            } else {
                setEditIssue({ kind: 'error', message: userErrorMessage(err, t) });
            }
        } finally {
            setSaving(false);
        }
    };

    const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
    const isActionable = status === 'pending';

    return (
        <div ref={cardRef} data-testid="calendar-booking-card" data-status={status} data-superseded={superseded && status === persistedStatus ? 'true' : undefined} data-series-count={series ? series.count : undefined} className={`relative p-3 min-w-0 max-w-full ${style.bg} border ${style.border} rounded-lg text-xs`}>
            {/* Top-right per-event controls (Calendar Ui #226): a compact Pencil
                (edit) + X (remove/decline) so, in a stack of many event cards, each
                event can be edited or removed individually before confirming. They
                mirror the bottom Edit/Decline handlers and only show while the card
                is still actionable and not already in the edit form. */}
            {isActionable && !isEditing && (
                <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                    {!series && <button
                        data-testid="calendar-card-edit"
                        onClick={() => setIsEditing(true)}
                        disabled={saving}
                        title={t('caleEditEventTooltip', 'Edit event')}
                        aria-label={t('caleEditEventTooltip', 'Edit event')}
                        className="p-1 rounded text-pai-meta hover:text-pai-blue hover:bg-white/[0.07] disabled:opacity-50 transition-colors"
                    >
                        <Pencil size={13} />
                    </button>}
                    <button
                        data-testid="calendar-card-remove"
                        onClick={handleDecline}
                        disabled={saving}
                        title={t('caleRemoveEventTooltip', 'Remove event')}
                        aria-label={t('caleRemoveEventTooltip', 'Remove event')}
                        className="p-1 rounded text-pai-meta hover:text-red-600 dark:hover:text-red-400 hover:bg-white/[0.07] disabled:opacity-50 transition-colors"
                    >
                        <X size={13} />
                    </button>
                </div>
            )}
            {/* Header */}
            <div className="flex items-center gap-2 mb-2 pr-12">
                <Calendar size={14} className="text-amber-600 dark:text-amber-400" />
                <span className="font-medium text-white">{t('caleCalendarEvent', 'Calendar Event')}</span>
                <span data-testid="calendar-booking-status" className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${style.badge}`}>
                    {superseded && status === persistedStatus
                        ? t('cardSuperseded', 'Replaced by the updated card')
                        : bookingStatusLabel(status, t)}
                </span>
            </div>

            {isEditing ? (
                /* Edit form */
                <div className="space-y-2 mt-2">
                    <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        placeholder={t('caleEventTitlePlaceholder', 'Event title')}
                        className="w-full text-sm px-2 py-1 border border-pai rounded bg-white/[0.06] text-white"
                    />
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <label className="text-[10px] text-pai-meta">{t('caleStartLabel', 'Start')}</label>
                            <input
                                type="datetime-local"
                                value={editStart}
                                onChange={(e) => setEditStart(e.target.value)}
                                className="w-full text-xs px-2 py-1 border border-pai rounded bg-white/[0.06] text-white"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="text-[10px] text-pai-meta">{t('caleEndLabel', 'End')}</label>
                            <input
                                type="datetime-local"
                                value={editEnd}
                                onChange={(e) => setEditEnd(e.target.value)}
                                className="w-full text-xs px-2 py-1 border border-pai rounded bg-white/[0.06] text-white"
                            />
                        </div>
                    </div>
                    <input
                        type="text"
                        value={editLocation}
                        onChange={(e) => setEditLocation(e.target.value)}
                        placeholder={t('caleLocationOptionalPlaceholder', 'Location (optional)')}
                        className="w-full text-sm px-2 py-1 border border-pai rounded bg-white/[0.06] text-white"
                    />
                    <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder={t('caleNotesOptionalPlaceholder', 'Notes (optional)')}
                        className="w-full text-sm px-2 py-1 border border-pai rounded bg-white/[0.06] text-white"
                    />
                    {/* Double-booking warning (#268) — the reschedule was refused; the user decides. */}
                    {editIssue?.kind === 'conflict' && (
                        <div
                            data-testid="booking-edit-conflict"
                            className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2 space-y-1"
                        >
                            <div className="text-[11px] font-semibold text-amber-900 dark:text-amber-200">
                                {t('caleConflictOverlapColon', 'This overlaps an event you have already confirmed:')}
                            </div>
                            <ul className="space-y-0.5">
                                {(editIssue.conflicts || []).map((c) => (
                                    <li key={c.id} className="text-[11px] text-amber-900 dark:text-amber-200 break-words">
                                        <span className="font-semibold">{c.title || t('caleUntitledEvent', 'Untitled event')}</span>
                                        {(c.start_local || c.end_local) && (
                                            <span className="ml-1 opacity-80">
                                                ({c.start_local}{c.end_local ? ` – ${c.end_local}` : ''})
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                data-testid="booking-edit-conflict-confirm"
                                onClick={() => handleSaveEdit(true)}
                                disabled={saving}
                                className="px-2.5 py-1 text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 rounded"
                            >
                                {t('caleBookAnyway', 'Book anyway')}
                            </button>
                        </div>
                    )}
                    {editIssue?.kind === 'error' && (
                        <div className="text-[11px] text-rose-600 dark:text-rose-400">{editIssue.message}</div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-1 min-w-0">
                        <button
                            onClick={() => handleSaveEdit(false)}
                            disabled={saving || !editTitle || !editStart}
                            className="flex items-center gap-1 px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium disabled:opacity-50 transition-colors"
                        >
                            <Check size={12} /> {t('caleSaveConfirm', 'Save & Confirm')}
                        </button>
                        <button
                            onClick={() => { setEditIssue(null); setIsEditing(false); }}
                            className="flex items-center gap-1 px-3 py-1 border border-pai rounded text-xs text-pai-secondary hover:bg-white/[0.07] transition-colors"
                        >
                            <X size={12} /> {t('commonCancel', 'Cancel')}
                        </button>
                    </div>
                </div>
            ) : (
                /* Display mode */
                <>
                    <div className={`space-y-1 ${status === 'declined' ? 'opacity-50' : ''}`}>
                        <div className={`text-sm font-medium text-white ${status === 'declined' ? 'line-through' : ''}`}>
                            {event.title}
                        </div>
                        <div className="flex items-center gap-1 text-pai-meta">
                            <Clock size={11} />
                            {isTimePending(event.start_time, event.end_time) ? (
                                <span>{formatEventDate(event.start_time)} · <span className="text-amber-600 dark:text-amber-400 font-medium">{t('caleTimeTbc', 'Time to be confirmed')}</span></span>
                            ) : (
                                <span>{formatTimeRange(event.start_time, event.end_time)}</span>
                            )}
                        </div>
                        {series && (
                            <div
                                data-testid="calendar-booking-series"
                                data-count={series.count}
                                className="flex items-start gap-1 text-pai-secondary"
                            >
                                <Repeat size={11} className="mt-0.5 shrink-0" />
                                <span className="break-words">{seriesSummary(series, t, language)}</span>
                            </div>
                        )}
                        {event.location && (
                            <div className="flex items-center gap-1 text-pai-meta">
                                <MapPin size={11} />
                                <span>{event.location}</span>
                            </div>
                        )}
                        {event.description && (
                            <div className="text-pai-meta italic mt-1">
                                {event.description}
                            </div>
                        )}
                    </div>

                    {/* Action buttons */}
                    {isActionable && (
                        // #517: the row wraps instead of overflowing a narrow card, and
                        // a narrow card (split view) shows the Edit icon only.
                        <div data-testid="card-action-row" className={CARD_ACTION_ROW.replace('mt-3', 'mt-2')}>
                            <button
                                data-testid="calendar-card-confirm"
                                onClick={handleConfirm}
                                disabled={saving}
                                className={`flex items-center gap-1 px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium disabled:opacity-50 transition-colors ${CARD_BTN_FIT}`}
                            >
                                <Check size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('commonConfirm', 'Confirm')}</span>
                            </button>
                            {!series && <button
                                onClick={() => setIsEditing(true)}
                                disabled={saving}
                                aria-label={t('commonEdit', 'Edit')}
                                title={t('commonEdit', 'Edit')}
                                className={`flex items-center gap-1 px-3 py-1 border border-pai rounded text-xs text-pai-secondary hover:bg-white/[0.07] disabled:opacity-50 transition-colors ${CARD_BTN_FIT}`}
                            >
                                <Pencil size={12} className={CARD_ICON} />
                                {!compact && <span className={CARD_BTN_LABEL}>{t('commonEdit', 'Edit')}</span>}
                            </button>}
                            <button
                                data-testid="calendar-card-decline"
                                onClick={handleDecline}
                                disabled={saving}
                                className={`flex items-center gap-1 px-3 py-1 border border-red-300 dark:border-red-700 rounded text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors ${CARD_BTN_FIT}`}
                            >
                                <X size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('contactDeclineBtn', 'Decline')}</span>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CalendarBookingCard;
